import React, { useState } from "react";
import { Button, Checkbox, Form, Icon } from "semantic-ui-react";

// Building blocks for the Property Intelligence report. They render the
// server-built presentation model (functions/propertyIntelligence/
// presentation.js): labelled sources, restrained status badges and the
// original findings with the agent's review controls.

const TONE_ICON = { positive: "check circle", warning: "exchange", caution: "warning sign", info: "clipboard check", neutral: "info circle" };

/** Restrained evidence badge. `display` = {label, tone} from the presentation model. */
export function StatusBadge({ display, icon = false }) {
  if (!display) return null;
  return (
    <span className={`pi-badge ${display.tone || "neutral"}`}>
      {icon && <Icon name={TONE_ICON[display.tone] || "info circle"} style={{ margin: 0 }} />}
      {display.label}
    </span>
  );
}

/** Labelled source links, best evidence first. Never shows raw URLs as text. */
export function SourceLinks({ sources, label = "Sources", max = 3, showTier = false }) {
  const list = (sources || []).filter((s) => s && s.label).slice(0, max);
  if (!list.length) return null;
  return (
    <div className="pi-sources">
      {label && <span className="pi-muted">{label}:</span>}
      {list.map((s) =>
        s.url ? (
          <a key={s.id || s.url} href={s.url} target="_blank" rel="noopener noreferrer" title={s.tierLabel || s.label}>
            {s.label}
            <Icon name="external alternate" />
          </a>
        ) : (
          <span key={s.id || s.label} className="pi-muted">{s.label}</span>
        )
      )}
      {showTier && list[0]?.tierLabel && <span className="pi-tier">({list[0].tierLabel})</span>}
    </div>
  );
}

export function LinkOut({ link, children }) {
  if (!link?.url) return null;
  return (
    <a className="pi-link" href={link.url} target="_blank" rel="noopener noreferrer">
      {children || link.label}
      <Icon name="external alternate" />
    </a>
  );
}

export function Card({ title, icon, count, right, sub, children, className = "", id }) {
  return (
    <section className={`pi-card ${className}`} id={id}>
      {title && (
        <h3 className="pi-card-title">
          {icon && <Icon name={icon} />}
          <span>{title}</span>
          {count !== undefined && <span className="pi-count">({count})</span>}
          {right && <span className="pi-right">{right}</span>}
        </h3>
      )}
      {sub && <p className="pi-sub">{sub}</p>}
      {children}
    </section>
  );
}

export function Expander({ open, onClick, label }) {
  return (
    <button type="button" className="pi-expander" onClick={onClick} aria-expanded={open} aria-label={open ? `Hide details: ${label}` : `Show details: ${label}`}>
      <Icon name={open ? "chevron up" : "chevron right"} />
    </button>
  );
}

/** Collapsible region with a text toggle (keyboard accessible). */
export function Disclosure({ label, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="pi-disclosure">
      <button type="button" className="pi-link" onClick={() => setOpen(!open)} aria-expanded={open}>
        {open ? "Hide" : label}
        {count !== undefined && !open ? ` (${count})` : ""}
        <Icon name={open ? "chevron up" : "chevron down"} />
      </button>
      {open && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  );
}

/**
 * One original research finding with the agent's review controls. Agent
 * edits are stored separately from the finding itself.
 */
export function FindingCard({ finding, onEdit }) {
  const [editingNote, setEditingNote] = useState(false);
  const [note, setNote] = useState(finding.edit?.agentNote || "");
  const [saving, setSaving] = useState(false);

  async function save(patch) {
    if (!onEdit) return;
    setSaving(true);
    try {
      await onEdit({ type: "finding", id: finding.id, ...patch });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ border: "1px solid #eef1f5", borderRadius: 10, padding: "12px 14px", marginBottom: 10, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div className="pi-row-title" style={{ fontSize: 15 }}>{finding.title}</div>
        <StatusBadge display={finding.display} />
      </div>
      {finding.statement && <p className="pi-p" style={{ margin: "6px 0 0", fontSize: 14.5 }}>{finding.statement}</p>}
      {finding.whyItMatters && (
        <p className="pi-small" style={{ margin: "6px 0 0" }}>
          <span className="pi-label">Why it matters: </span>
          {finding.whyItMatters}
        </p>
      )}
      {finding.recommendedAction && (
        <p className="pi-small" style={{ margin: "6px 0 0" }}>
          <span className="pi-label">Next step: </span>
          {finding.recommendedAction}
        </p>
      )}
      {(finding.notes || []).map((n) => (
        <p key={n} className="pi-small pi-muted" style={{ margin: "6px 0 0" }}>
          <Icon name="info circle" /> {n}
        </p>
      ))}
      <SourceLinks sources={finding.sources} max={4} />
      {finding.edit?.agentNote && !editingNote && (
        <div className="pi-note neutral" style={{ marginTop: 8 }}>
          <Icon name="user" />
          <div><strong>Your note:</strong> {finding.edit.agentNote}</div>
        </div>
      )}
      {onEdit && (
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", marginTop: 10, fontSize: 13.5 }}>
          <Checkbox label="Reviewed" checked={!!finding.edit?.reviewed} disabled={saving} onChange={(e, { checked }) => save({ reviewed: checked })} />
          <Checkbox label="Hide from client" checked={!!finding.edit?.hiddenFromClient} disabled={saving} onChange={(e, { checked }) => save({ hiddenFromClient: checked })} />
          {!editingNote && (
            <button type="button" className="pi-link" onClick={() => setEditingNote(true)}>
              {finding.edit?.agentNote ? "Edit note" : "Add note"}
            </button>
          )}
        </div>
      )}
      {editingNote && (
        <Form size="small" style={{ marginTop: 8 }}>
          <Form.TextArea rows={2} value={note} onChange={(e, { value }) => setNote(value)} placeholder="Your commentary on this finding" />
          <Button size="mini" primary loading={saving} onClick={async () => { await save({ agentNote: note }); setEditingNote(false); }} content="Save note" />
          <Button size="mini" basic onClick={() => setEditingNote(false)} content="Cancel" />
        </Form>
      )}
    </div>
  );
}

/** "Supporting evidence (n)" - the original findings behind a consolidated topic. */
export function EvidenceDisclosure({ findings, onEdit, label = "Supporting evidence" }) {
  if (!findings?.length) return null;
  return (
    <Disclosure label={label} count={findings.length}>
      {findings.map((f) => <FindingCard key={f.id} finding={f} onEdit={onEdit} />)}
    </Disclosure>
  );
}

export const THEME_ICONS = {
  squareFootage: "expand arrows alternate",
  permits: "file alternate outline",
  saleHistory: "exchange",
  propertyTax: "dollar sign",
  metroDistrict: "university",
  hoa: "building outline",
  amenities: "tree",
  soils: "warning sign",
  drainage: "tint",
  wildfire: "fire",
  environmental: "leaf",
  insurance: "shield alternate",
  buildingAge: "wrench",
  utilities: "plug",
  noise: "volume up",
  transit: "train",
  development: "building",
  schools: "graduation cap",
  safety: "shield",
  neighborhoodOther: "map outline",
  other: "info circle",
};

/** A consolidated topic: one summary plus its supporting evidence. */
export function TopicCard({ topic, onEdit }) {
  if (!topic) return null;
  return (
    <div className="pi-card">
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div className={`pi-row-icon ${topic.display.tone}`}><Icon name={THEME_ICONS[topic.theme] || "info circle"} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div className="pi-row-title" style={{ fontSize: 16 }}>{topic.title}</div>
            <StatusBadge display={topic.display} />
          </div>
          <p className="pi-p" style={{ margin: "6px 0 0" }}>{topic.summary}</p>
          {topic.whyItMatters && (
            <p className="pi-small" style={{ margin: "6px 0 0" }}><span className="pi-label">Why it matters: </span>{topic.whyItMatters}</p>
          )}
          {topic.action && (
            <p className="pi-small" style={{ margin: "6px 0 0" }}><span className="pi-label">Verify: </span>{topic.action}</p>
          )}
          <SourceLinks sources={topic.sources} />
          <EvidenceDisclosure findings={topic.findings} onEdit={onEdit} />
        </div>
      </div>
    </div>
  );
}
